/**
 * Rich Matching Editor
 * Two-tab editor: Lesson (Page) and Questions (Match)
 */

import * as React from 'react';
import { Input, Label, Textarea, cn } from '@ai-content-hub/content-kit';
import { type EditTabDescriptor, CompositeEditShell, CompositeEditContentTab, CompositeEditQuestionsLayout, useLocale } from '@ai-content-hub/content-core';
import {
    PageEdit,
    MatchEdit,
    type PageElementData,
    type MatchElementData,
} from '@ai-content-hub/content-elements';
import type { RichMatchingData, RichMatchingConfig } from './schema';

/** Editor display options - matches EditorDisplayOptions from content-core */
interface EditorDisplayOptions {
    showTitle?: boolean;
    showDescription?: boolean;
}
import { defaultRichMatchingData } from './schema';
import { BookOpen, Shuffle, Settings } from 'lucide-react';

type TabValue = 'lesson' | 'questions' | 'config';

export interface RichMatchingEditProps {
    /** Content data */
    value: RichMatchingData;
    /** Callback when data changes */
    onChange: (value: RichMatchingData) => void;
    /** Config (optional) */
    config?: RichMatchingConfig;
    /** Config change callback */
    onConfigChange?: (config: RichMatchingConfig) => void;
    /** Whether editing is disabled */
    disabled?: boolean;
    /** Editor display options (e.g. hide title/description fields) */
    editorOptions?: EditorDisplayOptions;
    /** Slot for Configuration panel (from ContentEditor when hasIntegratedConfigTab) */
    configSlot?: React.ReactNode;
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
    /** Additional class name */
    className?: string;
}

export const RichMatchingEdit: React.FC<RichMatchingEditProps> = ({
    value: data,
    onChange,
    config: _config,
    onConfigChange: _onConfigChange,
    disabled = false,
    editorOptions,
    configSlot,
    errors,
    className,
}) => {
    const [activeTab, setActiveTab] = React.useState<TabValue>('lesson');
    const { t } = useLocale({ config: _config?.i18n });

    const safeData: RichMatchingData = {
        ...defaultRichMatchingData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultRichMatchingData.lessonContent,
        matchData: data?.matchData ?? defaultRichMatchingData.matchData,
    };

    const matchCount = safeData.matchData?.items?.length ?? 0;

    const updateData = (updates: Partial<RichMatchingData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const handleLessonChange = (lessonContent: PageElementData) => {
        updateData({ lessonContent });
    };

    const handleMatchChange = (matchData: MatchElementData) => {
        updateData({ matchData });
    };

    const richMatchingTabs: EditTabDescriptor[] = [
        { id: 'lesson', label: t('lessonTab'), icon: BookOpen },
        { id: 'questions', label: t('questionsTab'), icon: Shuffle, badge: matchCount },
        ...(configSlot ? [{ id: 'config', label: t('configuration'), icon: Settings }] : []),
    ];

    const lessonMeta = (
        <div className="rich-matching-edit-lesson-form space-y-4">
            {editorOptions?.showTitle !== false && (
                    <div className="space-y-2">
                        <Label htmlFor="rich-matching-title">
                            {t('title')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="rich-matching-title"
                            value={safeData.title || ''}
                            onChange={(e) => updateData({ title: e.target.value })}
                            placeholder={t('enterTitlePlaceholder')}
                            disabled={disabled}
                            className="rich-matching-edit-title-input text-lg font-semibold"
                        />
                    </div>
                )}
                {editorOptions?.showDescription !== false && (
                    <div className="space-y-2">
                        <Label htmlFor="rich-matching-description">{t('descriptionOptional')}</Label>
                        <Textarea
                            id="rich-matching-description"
                            value={safeData.description || ''}
                            onChange={(e) => updateData({ description: e.target.value })}
                            placeholder={t('enterDescriptionPlaceholder')}
                            disabled={disabled}
                            rows={3}
                            className="rich-matching-edit-description-input"
                        />
                    </div>
                )}
        </div>
    );
    return (
        <CompositeEditShell
            prefix="rich-matching-edit"
            tabs={richMatchingTabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as TabValue)}
            configSlot={configSlot}
            className={cn('rich-matching-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'lesson') {
                    if (
                        editorOptions?.showTitle === false &&
                        editorOptions?.showDescription === false
                    ) {
                        return (
                            <div className="rich-matching-edit-content rich-matching-edit-lesson-panel flex-1 min-h-0 overflow-y-auto px-1">
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    className="rich-matching-edit-lesson-page-inner"
                                />
                            </div>
                        );
                    }
                    return (
                        <CompositeEditContentTab
                            prefix="rich-matching-edit"
                            meta={lessonMeta}
                            page={
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    className="rich-matching-edit-lesson-page-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    const matchItemsError = errors?.['matchData.items'];
                    return (
                        <CompositeEditQuestionsLayout
                            variant="detail-only"
                            prefix="rich-matching-edit"
                            detail={
                                <>
                                    {matchItemsError && (
                                        <p className="text-sm text-destructive mb-3" role="alert">
                                            {matchItemsError}
                                        </p>
                                    )}
                                    <MatchEdit
                                        value={safeData.matchData}
                                        onChange={handleMatchChange}
                                        disabled={disabled}
                                        className="rich-matching-edit-match-inner"
                                    />
                                </>
                            }
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default RichMatchingEdit;
